import React, { useState } from 'react'
import { SVGLoader } from 'three-stdlib'
import config from '../config'

export default function MapUpload({ onMapCreated, superAdmin = false, tenants = [], defaultTenantId = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    file: null,
    backgroundFile: null,
    overlayFile: null,
    tenantId: defaultTenantId || ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (file && file.type === 'image/svg+xml') {
      // Compute stable shape keys using SVGLoader before upload
      try {
        const text = await file.text()
        const loader = new SVGLoader()
        const data = loader.parse(text)
        const shapeKeys = []
        data.paths.forEach((p, pathIndex) => {
          const shapes = p.toShapes(true)
          shapes.forEach((_, shapeIndex) => {
            shapeKeys.push(`path-${pathIndex}-shape-${shapeIndex}`)
          })
        })
      setFormData(prev => ({ ...prev, file, shapeKeys }))
      } catch (err) {
        console.error('SVG parse failed', err)
      setFormData(prev => ({ ...prev, file, shapeKeys: undefined }))
      }
      setError('')
    } else {
      setError('Please select a valid SVG file')
    }
  }

  const handleBackgroundFileChange = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, backgroundFile: file }))
      setError('')
    } else {
      setError('Please select a valid image file for background')
    }
  }

  const handleOverlayFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) {
      setFormData(prev => ({ ...prev, overlayFile: null }))
      return
    }

    const lowerName = file.name.toLowerCase()
    const isSvg = file.type === 'image/svg+xml' || lowerName.endsWith('.svg')
    const isPng = file.type === 'image/png' || lowerName.endsWith('.png')
    const isJpg = file.type === 'image/jpeg' || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')

    if (isSvg || isPng || isJpg) {
      setFormData(prev => ({ ...prev, overlayFile: file }))
      setError('')
    } else {
      setError('Overlay must be SVG, PNG, or JPG')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.file || !formData.backgroundFile) {
      setError('Please fill in all fields and upload both SVG and background image')
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const uploadData = new FormData()
      uploadData.append('name', formData.name)
      uploadData.append('svg', formData.file)
      uploadData.append('background', formData.backgroundFile)
      if (formData.overlayFile) {
        uploadData.append('backgroundSvg', formData.overlayFile)
      }
      if (formData.shapeKeys) {
        uploadData.append('shapeKeys', JSON.stringify(formData.shapeKeys))
      }
      if (superAdmin) {
        uploadData.append('tenantId', formData.tenantId)
      }

      const endpoint = superAdmin ? '/super/maps/upload' : '/maps/upload'
      const response = await fetch(config.getApiUrl(endpoint), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadData
      })

      if (response.ok) {
        const result = await response.json()
        // console.log('Map uploaded:', result)
        onMapCreated()
        setFormData({ name: '', file: null, backgroundFile: null, overlayFile: null, tenantId: defaultTenantId || '' })
        const fileInput = document.getElementById('file-input')
        const bgInput = document.getElementById('background-input')
        const overlayInput = document.getElementById('overlay-input')
        if (fileInput) fileInput.value = ''
        if (bgInput) bgInput.value = ''
        if (overlayInput) overlayInput.value = ''
      } else {
        const error = await response.json()
        setError(error.error || 'Upload failed')
      }
    } catch (err) {
      console.error('Upload error:', err)
      setError('Upload failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ background: 'white', borderRadius: 8, padding: 24, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
      <form onSubmit={handleSubmit}>
        {superAdmin && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
              Tenant
            </label>
            <select
              value={formData.tenantId}
              onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
              style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              disabled={loading}
              required
            >
              <option value="">Choose tenant</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Map Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter map name"
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
            disabled={loading}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            SVG File *
          </label>
          <input
            id="file-input"
            type="file"
            accept=".svg,image/svg+xml"
            onChange={handleFileChange}
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
            disabled={loading}
          />
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Only SVG files are supported. Maximum size: 5MB
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Background Image *
          </label>
          <input
            id="background-input"
            type="file"
            accept="image/*"
            onChange={handleBackgroundFileChange}
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
            disabled={loading}
          />
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Upload a background image (JPG, PNG, etc.). Maximum size: 15MB
          </div>
        </div>

        {error && (
          <div style={{ background: '#fee', color: '#c33', padding: 12, borderRadius: 4, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="submit"
            disabled={loading || !formData.name || !formData.file || !formData.backgroundFile}
            style={{
              padding: '12px 24px',
              background: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 16
            }}
          >
            {loading ? 'Uploading...' : 'Upload Map'}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setFormData({ name: '', file: null, backgroundFile: null, overlayFile: null, tenantId: defaultTenantId || '' })
              const fileInput = document.getElementById('file-input')
              const bgInput = document.getElementById('background-input')
              const overlayInput = document.getElementById('overlay-input')
              if (fileInput) fileInput.value = ''
              if (bgInput) bgInput.value = ''
              if (overlayInput) overlayInput.value = ''
              setError('')
            }}
            style={{
              padding: '12px 24px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 16
            }}
            disabled={loading}
          >
            Clear
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Value Overlay (SVG/PNG/JPG) — optional
          </label>
          <input
            id="overlay-input"
            type="file"
            accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
            onChange={handleOverlayFileChange}
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
            disabled={loading}
          />
          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
            Upload an overlay that contains labels/values rendered above the background (transparent PNG/JPG or SVG)
          </div>
        </div>
      </form>

      <div style={{ marginTop: 24, padding: 16, background: '#f8f9fa', borderRadius: 4 }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Upload Instructions:</h4>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: '#666' }}>
          <li>Upload an SVG file containing your map/layout</li>
          <li>Upload a background image that will be displayed behind the map</li>
          <li>Each &lt;path&gt; element will become a region</li>
          <li>Regions will be automatically created and can be edited after upload</li>
          <li>You can set lot numbers, sizes, prices, and status for each region</li>
        </ul>
      </div>
    </div>
  )
}
